/**
 * Utility functions for handling session storage and URL parameter decoding
 */

/**
 * The name of the URL parameter that contains the encrypted token data
 */
export const ENCODED_DATA_PARAM = 'token';

/**
 * Interface for the decoded JSON data structure
 */
export interface EncodedDataParams {
  AgentName?: string;
  AgencyEmail?: string;
  [key: string]: any;
}

/**
 * Mapping of JSON keys to session storage keys
 */
export const STORAGE_KEY_MAPPINGS: Record<string, string> = {
  AgentName: 'agentName',
  AgencyEmail: 'agencyEmail',
  // PHP uses different field names
  ClinicName: 'agentName',                 // ClinicName from PHP -> agentName in storage (Distributor Name)
  UserContactEmail: 'agencyEmail',         // UserContactEmail from PHP -> agencyEmail in storage
  AgencyContactPerson: 'clinicName',       // AgencyContactPerson from PHP -> clinicName in storage (backup)
};

// API base URL - use environment variable or default based on current host
// For Create React App, environment variables must start with REACT_APP_
declare const process: {
  env: {
    REACT_APP_API_URL?: string;
    NODE_ENV?: string;
  };
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
    ? 'http://localhost:4000' 
    : 'https://node-api-backend.kindforest-59dfedfc.japaneast.azurecontainerapps.io');

/**
 * Decrypts token using backend API (AES-256-GCM)
 * @param token Encrypted token from URL
 * @returns Object with decrypted parameters or null if failed
 */
export const decryptTokenViaApi = async (token: string): Promise<EncodedDataParams | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/token/decrypt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Token decryption failed:', errorData.error || response.statusText);
      return null;
    }

    const result = await response.json();
    
    // Store JWT token for session authentication
    if (result.token) {
      sessionStorage.setItem('session-jwt-token', result.token);
    }
    
    return result.data || null;
  } catch (error) {
    console.error('Error calling token decryption API:', error);
    return null;
  }
};

/**
 * Extracts token from URL
 * @returns Token string or null if not found
 */
export const extractTokenFromUrl = (): string | null => {
  const queryString = window.location.search;
  if (!queryString) return null;

  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(ENCODED_DATA_PARAM);
};

/**
 * Saves URL parameters to session storage
 * @param params Parameters to save
 */
export const saveParamsToSession = (params: EncodedDataParams): void => {
  try {
    // Map JSON data to session storage keys
    Object.entries(STORAGE_KEY_MAPPINGS).forEach(([jsonKey, storageKey]) => {
      if (params[jsonKey]) {
        sessionStorage.setItem(storageKey, params[jsonKey]);
      }
    });
    
    // Set a flag indicating params were processed (session only)
    sessionStorage.setItem('paramsProcessed', 'true');
  } catch (e) {
    console.error('Error saving to localStorage:', e);
  }
};

/**
 * Processes URL parameters and saves to session storage
 * Always processes when token is present in URL to get fresh JWT session
 * This is now async and returns a Promise
 * @returns True if parameters were processed, false if no token found
 */
export const processUrlParams = async (): Promise<boolean> => {
  const token = extractTokenFromUrl();

  if (!token) {
    return false;
  }

  // Always decrypt token via API when present in URL
  const params = await decryptTokenViaApi(token);

  // Check if any parameters were found
  if (params && Object.keys(params).length > 0) {
    saveParamsToSession(params);
    
    // Dispatch event immediately so listeners can update
    window.dispatchEvent(new Event('params-processed'));
    return true;
  }

  return false;
};
